import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  OutlinedInput,
  Select,
  TextField,
} from '@mui/material'
import React from 'react'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { inject } from 'mobx-react'
import { IStores } from 'src/Stores'
import moment from 'moment'
import { InboxActionSummary } from 'src/viewModels/workflow/InboxActionSummary'
import { StaticAction } from 'src/utils/StaticAction'
import { ProcessAction } from 'src/viewModels/workflow/ProcessAction'
import { processAction } from 'src/services/WorkflowService'
import { UserDropdownOption } from 'src/viewModels/UserDropdownOption'
import { Form, Formik, FormikProps } from 'formik'
import { assignPendValidationSchema } from '../../validationSchemas/AssignPendValidationSchema'

interface IAssignPendModalProps {
  isOpen: boolean
  closeModal: () => void
  action: InboxActionSummary
  getUserOptions?: (inboxId: string, actionId: string) => void
  userOptions?: UserDropdownOption[]
  rerouteToPath?: (path: string) => void
  inboxItemId: number
  inboxId: string
  handleProcessActionFinish?: (selectedAction?: InboxActionSummary) => void
}

interface IAssignPendModalState {
  executed: boolean
  isLoading: boolean
}

interface IAssignPendFormValues {
  assignUser: string
  assignDueDate?: string
  pendDate?: string
  pendNote: string
  comments: string
  requireComment: boolean
  isAssign: boolean
  isPend: boolean
}

class AssignPendModal extends React.Component<
  IAssignPendModalProps,
  IAssignPendModalState
> {
  public state: IAssignPendModalState = {
    executed: false,
    isLoading: true,
  }

  private formikRef = React.createRef<FormikProps<IAssignPendFormValues>>()

  public componentDidMount(): void {
    if (this.props.action.staticActionId == StaticAction.ASSIGN) {
      this.props.getUserOptions?.(this.props.inboxId, this.props.action.id.toString())
    }

    this.setState({ isLoading: false })
  }

  private async save(values: any) {
    this.setState({ executed: true, isLoading: true })

    const action = this.props.action
    const data = new ProcessAction()

    if (action.staticActionId === StaticAction.PEND) {
      data.pendDate = new Date(moment(values.pendDate, 'YYYY-MM-DD').toISOString())
      data.pendNote = values.pendNote
    } else {
      if (values.assignDueDate) {
        data.assignDueDate = new Date(
          moment(values.assignDueDate, 'YYYY-MM-DD').toISOString()
        )
      }
      data.assignUser = values.assignUser
    }

    data.comment = values.comments

    processAction(data, action.id.toString(), this.props.inboxItemId.toString())
      .then(() => {
        this.props.handleProcessActionFinish?.(action)
        this.setState({ isLoading: false })
        this.props.closeModal()
      })
      .catch(() => {
        this.setState({ executed: false, isLoading: false })
        this.close()
      })
  }

  private close = () => {
    this.setState({
      executed: false,
    })
    this.props.closeModal()
  }

  public render() {
    const modalTitle = this.props.action.actionName.toUpperCase()

    const MenuProps = {
      getContentAnchorEl: null,
      PaperProps: {
        style: {
          maxHeight: 400,
        },
      },
    }

    return (
      <>
        <Dialog
          key={this.props.action.id}
          fullWidth={true}
          maxWidth="sm"
          open={this.props.isOpen}
          onClose={(_event, reason) => {
            if (reason === 'backdropClick') return this.props.closeModal()
          }}
        >
          <DialogTitle style={{ padding: 0 }}>
            <ResponsiveAppBar title={modalTitle} isModal={true} />
          </DialogTitle>
          <DialogContent style={{ padding: '12px 12px 8px' }}>
            {this.state.isLoading && (
              <div
                style={{
                  position: 'fixed',
                  display: 'flex',
                  width: '100%',
                  height: '100%',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  cursor: 'pointer',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1200,
                }}
              >
                <CircularProgress size={100} />
              </div>
            )}
            <Formik
              innerRef={this.formikRef}
              initialValues={{
                assignUser: '',
                assignDueDate: undefined,
                pendDate: undefined,
                pendNote: '',
                comments: '',
                requireComment: this.props.action.requireComment,
                isAssign: this.props.action.staticActionId === StaticAction.ASSIGN,
                isPend: this.props.action.staticActionId === StaticAction.PEND,
              }}
              validationSchema={assignPendValidationSchema}
              validateOnBlur={true}
              validateOnChange={true}
              onSubmit={(values) => {
                this.save(values)
              }}
            >
              {({ values, handleChange, handleBlur, errors, isValid, touched }) => (
                <Form>
                  {!this.state.executed && (
                    <Grid container direction="column" spacing={2}>
                      {this.props.action.staticActionId == StaticAction.ASSIGN && (
                        <>
                          <Grid item xs={12}>
                            <FormControl
                              fullWidth
                              variant="outlined"
                              error={touched.assignUser && !!errors.assignUser}
                            >
                              <InputLabel shrink={true} variant="outlined">
                                User *
                              </InputLabel>
                              <Select
                                variant="outlined"
                                input={<OutlinedInput notched label="User *" />}
                                native={true}
                                name="assignUser"
                                value={values.assignUser}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required={values.isAssign}
                                MenuProps={MenuProps}
                              >
                                <option value="">Select a user</option>
                                {this.props.userOptions?.map((user) => (
                                  <option key={user.id} value={user.id}>
                                    {user.description}
                                  </option>
                                ))}
                              </Select>
                              {touched.assignUser && errors.assignUser && (
                                <FormHelperText>{errors.assignUser}</FormHelperText>
                              )}
                            </FormControl>
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              inputProps={{ id: 'assignDueDate' }}
                              InputLabelProps={{ shrink: true }}
                              type="date"
                              name="assignDueDate"
                              variant="outlined"
                              value={values.assignDueDate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              label="Due Date"
                              fullWidth
                              error={touched.assignDueDate && !!errors.assignDueDate}
                              helperText={touched.assignDueDate && errors.assignDueDate}
                            />
                          </Grid>
                        </>
                      )}
                      {this.props.action.staticActionId == StaticAction.PEND && (
                        <>
                          <Grid item xs={12}>
                            <TextField
                              inputProps={{ id: 'pendDate' }}
                              InputLabelProps={{ shrink: true }}
                              type="date"
                              name="pendDate"
                              variant="outlined"
                              value={values.pendDate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              label="Pend Date"
                              fullWidth
                              error={touched.pendDate && !!errors.pendDate}
                              helperText={touched.pendDate && errors.pendDate}
                              required={values.isPend}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label="Pend Note"
                              name="pendNote"
                              variant="outlined"
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              required={true}
                              multiline
                              minRows="1"
                              value={values.pendNote}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.pendNote && !!errors.pendNote}
                              helperText={touched.pendNote && errors.pendNote}
                            />
                          </Grid>
                        </>
                      )}
                      {(this.props.action.staticActionId == StaticAction.ASSIGN ||
                        this.props.action.displayComment) && (
                        <Grid item xs={12}>
                          <TextField
                            label="Comments"
                            name="comments"
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            required={values.requireComment}
                            fullWidth
                            multiline
                            minRows="4"
                            value={values.comments}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.comments && !!errors.comments}
                            helperText={touched.comments && errors.comments}
                          />
                        </Grid>
                      )}
                    </Grid>
                  )}
                  <DialogActions>
                    {!this.state.executed && (
                      <Button
                        variant="contained"
                        color="primary"
                        style={{ marginLeft: '4px' }}
                        type="submit"
                        disabled={this.state.executed || !isValid}
                      >
                        Execute
                      </Button>
                    )}
                    <Button onClick={this.close} data-cy="close">
                      Close
                    </Button>
                  </DialogActions>
                </Form>
              )}
            </Formik>
          </DialogContent>
        </Dialog>
      </>
    )
  }
}

const InjectedAssignPendModal = inject<
  IStores,
  IAssignPendModalProps,
  Partial<IAssignPendModalProps>,
  any
>((stores: IStores) => ({
  getUserOptions: stores.users.getUserOptions,
  userOptions: stores.users.userOptions,
  rerouteToPath: stores.global.rerouteToPath,
}))(AssignPendModal)

export default InjectedAssignPendModal
